'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface Testimonial {
  id: number;
  name: string;
  role: string;
  image: string;
  alt: string;
  rating: number;
  text: string;
  location: string;
  service: string;
}

const TestimonialsSection = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  const testimonials: Testimonial[] = [
  {
    id: 1,
    name: 'રમેશભાઈ પટેલ',
    role: 'Service Beneficiary',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1c45de680-1763295122064.png",
    alt: 'Middle-aged Indian man with gray hair and mustache wearing white kurta smiling warmly',
    rating: 5,
    text: 'સેવા સેતુ ટ્રસ્ટે મારા પરિવારને ખૂબ જ મદદ કરી છે. તેમની આરોગ્ય તપાસ સેવા ખૂબ જ સારી અને સસ્તી છે. ડોક્ટરો ખૂબ જ સારી રીતે સારવાર કરે છે અને સ્ટાફ પણ ખૂબ જ મદદરૂપ છે.',
    location: 'Sarthana, Surat',
    service: 'Health Checkup Package'
  },
  {
    id: 2,
    name: 'Priya Sharma',
    role: 'Volunteer',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1c48da832-1763299032357.png",
    alt: 'Young Indian woman with long black hair wearing blue traditional dress smiling at camera',
    rating: 5,
    text: 'Volunteering with Seva Setu has been the most rewarding experience of my life. The way they treat every beneficiary with dignity and respect is truly inspiring. I have learned so much about community service and compassion.',
    location: 'Varachha, Surat',
    service: 'Community Volunteer'
  },
  {
    id: 3,
    name: 'અમિતભાઈ શાહ',
    role: 'Regular Donor',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_12c863552-1763294002377.png",
    alt: 'Professional Indian man in navy blue suit with glasses smiling confidently in office setting',
    rating: 5,
    text: 'મેં ઘણી સંસ્થાઓને દાન આપ્યું છે, પરંતુ સેવા સેતુ ટ્રસ્ટ સૌથી પારદર્શક અને વિશ્વાસપાત્ર છે. તેઓ દરેક રૂપિયાનો હિસાબ આપે છે અને સમાજમાં વાસ્તવિક ફેરફાર લાવે છે.',
    location: 'Adajan, Surat',
    service: 'Monthly Donor'
  },
  {
    id: 4,
    name: 'Meera Desai',
    role: 'Service Beneficiary',
    image: "https://images.unsplash.com/photo-1617593461584-4d51cc5cff4c",
    alt: 'Elderly Indian woman with white hair wearing colorful saree smiling gently with kind eyes',
    rating: 5,
    text: 'The clothing distribution program helped my family during difficult times. The staff treated us with such respect and kindness. They not only provided clothes but also restored our dignity and hope.',
    location: 'Kadodara, Surat',
    service: 'Clothing Distribution'
  },
  {
    id: 5,
    name: 'રાજેશભાઈ વોરા',
    role: 'Community Leader',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_125b68624-1763295869657.png",
    alt: 'Senior Indian man with white beard wearing traditional white kurta with warm smile',
    rating: 5,
    text: 'સેવા સેતુ ટ્રસ્ટ અમારા સમુદાયમાં એક આશાનો કિરણ છે. તેમના આરોગ્ય શિબિરો અને સામુદાયિક કાર્યક્રમો હજારો પરિવારોને મદદ કરે છે. તેમની સેવા ખરેખર પ્રેરણાદાયક છે.',
    location: 'Sarthana, Surat',
    service: 'Community Partner'
  },
  {
    id: 6,
    name: 'Anjali Patel',
    role: 'Yoga Participant',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_17f8f5ea4-1763299033127.png",
    alt: 'Young Indian woman with short black hair in fitness attire smiling brightly outdoors',
    rating: 5,
    text: 'The free yoga and wellness programs have transformed my life. I was struggling with stress and health issues, but the expert instructors and supportive community helped me regain my physical and mental health.',
    location: 'Varachha, Surat',
    service: 'Wellness Program'
  }];


  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (!isHydrated) return;

    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 6000);

    return () => clearInterval(timer);
  }, [isHydrated, testimonials.length]);

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  if (!isHydrated) {
    return (
      <section className="py-12 md:py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-8 md:mb-16">
            <h2 className="text-2xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground mb-4">
              Community Stories & Impact
            </h2>
            <p className="text-lg text-muted-foreground">
              Real experiences from our beneficiaries, volunteers, and supporters
            </p>
          </div>
        </div>
      </section>);

  }

  const currentTestimonial = testimonials[currentIndex];

  return (
    <section className="py-12 md:py-20 bg-background">
      <div className="container mx-auto px-4">
        <div className="text-center max-w-3xl mx-auto mb-8 md:mb-16">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <Icon name="StarIcon" size={24} variant="solid" className="text-accent" />
            <span className="text-2xl font-heading font-bold text-foreground">4.9/5</span>
            <span className="text-muted-foreground">from 535+ reviews</span>
          </div>
          <h2 className="text-2xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground mb-4">
            Community Stories & Impact
          </h2>
          <p className="text-lg text-muted-foreground">
            Real experiences from our beneficiaries, volunteers, and supporters
          </p>
        </div>

        <div className="max-w-5xl mx-auto">
          <div className="glass-card bg-gradient-to-br from-primary/5 via-secondary/5 to-accent/5 rounded-3xl p-5 sm:p-8 md:p-12 shadow-glass">
            <div className="grid md:grid-cols-3 gap-8 items-center">
              <div className="flex flex-col items-center space-y-4">

                <div className="relative">
                  <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-full overflow-hidden ring-4 ring-primary/20">
                    <AppImage
                      key={currentTestimonial.id}
                      src={currentTestimonial.image}
                      alt={currentTestimonial.alt}
                      className="w-full h-full object-cover" />

                  </div>
                  <div className="absolute -bottom-2 -right-2 w-12 h-12 rounded-full bg-primary flex items-center justify-center shadow-cta">
                    <Icon name="CheckBadgeIcon" size={24} variant="solid" className="text-primary-foreground" />
                  </div>
                </div>
                <div className="text-center">
                  <h3 className="text-xl font-heading font-bold text-foreground">
                    {currentTestimonial.name}
                  </h3>
                  <p className="text-sm text-muted-foreground">{currentTestimonial.role}</p>
                  <div className="flex items-center justify-center space-x-1 mt-2">
                    {[...Array(currentTestimonial.rating)].map((_, i) =>
                    <Icon key={i} name="StarIcon" size={16} variant="solid" className="text-accent" />
                    )}
                  </div>
                </div>
              </div>

              <div className="md:col-span-2 space-y-6">
                <div className="relative bg-card p-6 sm:p-8 rounded-2xl rounded-tl-none md:rounded-3xl md:rounded-tl-none shadow-sm border border-border">
                  <p className="text-lg sm:text-xl text-foreground leading-relaxed relative z-10">
                    "{currentTestimonial.text}"
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-4 border-t border-border">
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                      <Icon name="MapPinIcon" size={16} className="text-primary" />
                      <span>{currentTestimonial.location}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                      <Icon name="CheckCircleIcon" size={16} className="text-secondary" />
                      <span>{currentTestimonial.service}</span>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    <button
                      onClick={handlePrevious}
                      className="w-10 h-10 rounded-full glass hover:bg-primary hover:text-primary-foreground transition-all duration-300 flex items-center justify-center shadow-glass"
                      aria-label="Previous testimonial">

                      <Icon name="ChevronLeftIcon" size={20} />
                    </button>
                    <button
                      onClick={handleNext}
                      className="w-10 h-10 rounded-full glass hover:bg-primary hover:text-primary-foreground transition-all duration-300 flex items-center justify-center shadow-glass"
                      aria-label="Next testimonial">

                      <Icon name="ChevronRightIcon" size={20} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Dots Indicator */}
          <div className="flex items-center justify-center space-x-2 mt-8">
            {testimonials.map((_, index) =>
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`h-2 rounded-full transition-all duration-300 ${
              index === currentIndex ? 'w-8 bg-primary' : 'w-2 bg-muted'}`
              }
              aria-label={`Go to testimonial ${index + 1}`} />

            )}
          </div>
        </div>

        {/* Trust Badges */}
        <div className="max-w-4xl mx-auto mt-16">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6">
            <div className="glass-card rounded-2xl p-6 text-center shadow-glass">
              <Icon name="ShieldCheckIcon" size={32} variant="solid" className="text-primary mx-auto mb-2" />
              <p className="text-sm font-semibold text-foreground">Verified Trust</p>
            </div>
            <div className="glass-card rounded-2xl p-6 text-center shadow-glass">
              <Icon name="CheckBadgeIcon" size={32} variant="solid" className="text-secondary mx-auto mb-2" />
              <p className="text-sm font-semibold text-foreground">JustDial Verified</p>
            </div>
            <div className="glass-card rounded-2xl p-6 text-center shadow-glass">
              <Icon name="DocumentCheckIcon" size={32} variant="solid" className="text-accent mx-auto mb-2" />
              <p className="text-sm font-semibold text-foreground">Registered NGO</p>
            </div>
            <div className="glass-card rounded-2xl p-6 text-center shadow-glass">
              <Icon name="HeartIcon" size={32} variant="solid" className="text-supporting mx-auto mb-2" />
              <p className="text-sm font-semibold text-foreground">Community Trusted</p>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

export default TestimonialsSection;